import {
  FlexBox,
  LoadingButton,
  SectionLoader,
  SelectableCard,
} from '@airship-platform/jam-web/components';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useAppointmentProposals } from '../../../data-access/appointmentProposals';
import { useJobImagesProvider } from '../../../data-access/jobImages';
import { SectionWrapper } from '../../../patterns';

const PHOTO_BOX_SIZE = '150px';
const INITIAL_VISIBLE_COUNT = 10;

const JobMediaPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentProposal } = useAppointmentProposals();
  const { availableImages, isLoading, isLoaded, isAttaching, attachJobImages } =
    useJobImagesProvider();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showAll, setShowAll] = useState(false);

  const toggleSelection = useCallback((jobFileId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(jobFileId)) {
        next.delete(jobFileId);
      } else {
        next.add(jobFileId);
      }
      return next;
    });
  }, []);

  if (!currentProposal) {
    return null;
  }

  const handleAttach = async () => {
    if (selectedIds.size === 0) return;
    await attachJobImages(
      currentProposal.appointmentId,
      currentProposal.appointmentProposalId,
      [...selectedIds]
    );
    setSelectedIds(new Set());
    navigate('../capture-media');
  };

  const images = availableImages;
  const hasMore = images.length > INITIAL_VISIBLE_COUNT;
  const visibleImages = showAll
    ? images
    : images.slice(0, INITIAL_VISIBLE_COUNT);
  const hiddenCount = images.length - INITIAL_VISIBLE_COUNT;

  const renderContent = () => {
    if (isLoading && !isLoaded) {
      return <SectionLoader isLoading title="Loading job photos..." />;
    }

    if (isLoaded && availableImages.length === 0) {
      return (
        <Alert severity="info">
          All job photos have already been added to this proposal.
        </Alert>
      );
    }

    if (!isLoaded) return null;

    return (
      <FlexBox flexDirection="column" gap={2} width="100%">
        <FlexBox gap={2} flexWrap="wrap">
          {visibleImages.map((image) => (
            <SelectableCard
              key={image.jobFileId}
              selected={selectedIds.has(image.jobFileId)}
              width={PHOTO_BOX_SIZE}
              height={PHOTO_BOX_SIZE}
              p={0.5}
              onClick={() => toggleSelection(image.jobFileId)}
            >
              <Box
                component="img"
                src={image.url}
                alt={image.fileName || image.title || 'Job photo'}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: 2,
                }}
              />
            </SelectableCard>
          ))}
        </FlexBox>

        {hasMore && (
          <Button color="softGold" onClick={() => setShowAll((prev) => !prev)}>
            {showAll ? 'Show Less' : `Show ${hiddenCount} More Photos`}
          </Button>
        )}
      </FlexBox>
    );
  };

  return (
    <SectionWrapper
      maxWidth={'md'}
      width="100%"
      header={`Job Photos (${images.length})`}
      subheader="Select photos from the job to include in this proposal."
      actionsButtons={{
        left: {
          color: 'softGold',
          component: Link,
          to: '../capture-media',
          label: 'Cancel',
        },
        right: {
          component: LoadingButton,
          label:
            selectedIds.size > 0
              ? `Attach Selected (${selectedIds.size})`
              : 'Attach Selected',
          loading: isAttaching,
          disabled: selectedIds.size === 0 || isAttaching,
          onClick: handleAttach,
        },
      }}
    >
      {renderContent()}
    </SectionWrapper>
  );
};

export default JobMediaPage;
